import { useState, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import * as F from './FindEmail.style';
import theme from '@styles/theme';
import useEnter from '@hooks/useEnter';
import { onlyNumber } from '@utils/common';
import { Mutation } from '@graphql/types';
import { FIND_ADMIN_ACCOUNT } from '@graphql/mutations';
import { validateFindEmail } from '@utils/validation';

import StyledInput from '@components/styled/StyledInput';
import StyledButton from '@components/styled/StyledButton';

const FindEmail = () => {
    const navigate = useNavigate();
    const onEnter = useEnter();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<string[] | null>(null);
    const [inputs, setInputs] = useState({
        name: '',
        cellphone: ''
    });

    const [findAdminAccount] =
        useMutation<Pick<Mutation, 'findAdminAccount'>>(FIND_ADMIN_ACCOUNT);

    const onChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            if (name === 'cellphone') {
                return setInputs({
                    ...inputs,
                    [name]: onlyNumber(value)
                });
            }
            setInputs({
                ...inputs,
                [name]: value
            });
        },
        [inputs]
    );

    const handleSubmit = async () => {
        if (!validateFindEmail(inputs)) return;

        const { name, cellphone } = inputs;

        setLoading(true);

        try {
            await findAdminAccount({
                variables: {
                    name,
                    cellphone
                },
                onCompleted: (data) => {
                    setResult(data.findAdminAccount as string[]);
                }
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <F.Wrapper>
            <F.Title>이메일을 잊으셨나요?</F.Title>
            <F.SubTitle>등록된 이름과 휴대폰번호를 입력해주세요</F.SubTitle>
            <StyledInput
                value={inputs.name}
                name="name"
                onChange={onChange}
                placeholder=" 이름을 입력해주세요"
                margin="0 0 8px"
                borderRadius={0}
                COLUMN
            />
            <StyledInput
                value={inputs.cellphone}
                name="cellphone"
                onChange={onChange}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                    onEnter(e, handleSubmit)
                }
                placeholder="휴대폰번호 (-)없이 숫자만 입력 해주세요"
                maxLength={11}
                borderRadius={0}
                COLUMN
            />
            <StyledButton
                title="찾기"
                margin="32px 0"
                width={343}
                fontSize={14}
                fontFamily="PretendardRegular"
                onClick={handleSubmit}
                borderRadius={0}
                loading={loading}
            />
            {result && (
                <F.SearchArea>
                    <F.SearchText>검색결과</F.SearchText>
                    {!result.length ? (
                        <F.Empty>검색 결과가 존재하지 않습니다.</F.Empty>
                    ) : (
                        <>
                            <F.Result>{result[0]}</F.Result>
                            <StyledButton
                                title="로그인하러 가기"
                                margin="32px 0"
                                width={343}
                                fontSize={14}
                                fontFamily="PretendardRegular"
                                fontColor={theme.colors.blackColor}
                                bgColor={theme.colors.whiteColor}
                                border={`${theme.colors.ultraLightGrayBorder} 1px solid`}
                                onClick={() => navigate('/')}
                                borderRadius={0}
                            />
                        </>
                    )}
                </F.SearchArea>
            )}
        </F.Wrapper>
    );
};

export default FindEmail;
